import MobileAppBar from '@/components/molecules/MobileAppBar';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import binIcon from '@/assets/icons/bin.svg';
import budgetIcon from '@/assets/icons/budget.svg';
import editIcon from '@/assets/icons/edit.svg';
import percentageIcon from '@/assets/icons/percentage.svg';
import euroIcon from '@/assets/icons/euro.svg';
import vaultIcon from '@/assets/icons/vault.svg';
import plusIcon from '@/assets/icons/plus.svg';
import buildingIcon from '@/assets/icons/building.svg';
import noInvoiceIcon from '@/assets/icons/no-invoice.svg';
import departmentACIcon from '@/assets/icons/department-acs.svg';
import productsACIcon from '@/assets/icons/product-and-services.svg';
import projectsACIcon from '@/assets/icons/project-acs.svg';
import contractsACIcon from '@/assets/icons/contracts-acs.svg';
import expenseIcon from '@/assets/icons/expense.svg';
import revenueIcon from '@/assets/icons/revenue.svg';

import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ChartConfig } from '@/components/ui/chart';
import PieGraph from '@/components/organisms/graphs/PieGraph';
import BudgetGeneralInfoAccordion from '@/components/molecules/BudgetGeneralInfoAccordion';
import OverviewList, { ListItem } from '@/components/organisms/OverviewList';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { useDeviceType } from '@/hooks/useDeviceType';
import BudgetInfoCard from '@/components/molecules/BudgetInfoCard';
import { budgetService } from '@/services/budget.service';
import { formatDate, getCurrencyInfo } from '@/utils/helpers';
import InfoModal from '@/components/molecules/InfoModal';
import deleteBudgetIcon from '@/assets/icons/delete-budget.svg';
import toast from 'react-hot-toast';
import { bankAccountService } from '@/services/bank-accounts.service';

import toggleACOffIcon from '@/assets/icons/ac-toggle-off.svg';

// import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setBreadcrumbs } from '@/store/slices/breadcrumb';
import { PAGE } from '@/routes';
import { accountingCenterService } from '@/services/accounting-centers.service';
import AccountCenterToggleItem from '@/components/molecules/AccountCenterToggleItem';
import ToggleItem from '@/components/molecules/ToggleItem';
import { expenseRevenueService } from '@/services/expense-category.service';
import { revenueCategoryService } from '@/services/revenue-category.service';

const ACCOUNTING_CENTERS = [
  {
    title: 'Department Account Center',
    value: 'DEPARTMENT_ACCOUNTING',
    icon: departmentACIcon
  },
  {
    title: 'Products & Services Accounting Center',
    value: 'PRODUCTS_AND_SERVICES',
    icon: productsACIcon
  },
  {
    title: 'Projects Accounting Center',
    value: 'PROJECTS',
    icon: projectsACIcon
  },
  {
    title: 'Contract Accounting Centers',
    value: 'CONTRACTS',
    icon: contractsACIcon
  }
];

const EXPENSE_ITEM = {
  title: 'Expense Categories',
  value: 'EXPENSE',
  icon: expenseIcon
};

const REVENUE_ITEM = {
  title: 'Revenue Categories',
  value: 'REVENUE',
  icon: revenueIcon
};

const DetailPage = () => {
  const [date, setDate] = useState<Date | undefined>(new Date());
  const { isMobileView } = useDeviceType();
  const { budgetId } = useParams();
  const dispatch = useDispatch();
  // const budgetId = id || '';
  const navigate = useNavigate();

  const [budget, setBudget] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);
  const [graphViewMode, setGraphViewMode] = useState('percentage');
  const [bankAccounts, setBankAccounts] = useState<any>([]);

  const [accountingCenters, setAccountingCenters] = useState<any>([]);
  const [checked, setChecked] = useState(false);
  const [loadingAccountingCenters, setLoadingAccountingCenters] = useState(false);

  const [accountingCentersToggle, setAccountingCentersToggle] = useState<any>({
    DEPARTMENT_ACCOUNTING: false,
    PRODUCTS_AND_SERVICES: false,
    PROJECTS: false,
    CONTRACTS: false
  });

  const [deactivateACConfirmModal, setDeactivateACConfirmModal] = useState(false);
  const [selectedAC, setSelectedAC] = useState<any>(null);

  const [expenseToggle, setExpenseToggle] = useState<boolean>(false);
  const [revenueToggle, setRevenueToggle] = useState<boolean>(false);
  const [loadingExpense, setLoadingExpense] = useState<boolean>(false);
  const [loadingRevenue, setLoadingRevenue] = useState<boolean>(false);

  const [deactivateExpenseConfirmModal, setDeactivateExpenseConfirmModal] = useState(false);
  const [deactivateRevenueConfirmModal, setDeactivateRevenueConfirmModal] = useState(false);

  const handleCheckChange = (checked: boolean) => {
    setChecked(checked);
  };

  const fetchAccountingCenters = async () => {
    setLoadingAccountingCenters(true);
    try {
      const { data } = await accountingCenterService.getAccountingCenters(budgetId || '');
      setAccountingCenters(data);

      // Initialize toggle states based on data from API
      const newToggleState = { ...accountingCentersToggle };

      // Update toggle state based on fetched data
      data.forEach((center: any) => {
        if (center && center.type && center.is_default) {
          newToggleState[center.type] = center.is_default || false;
        }
      });

      setAccountingCentersToggle(newToggleState);
    } catch (err) {
      console.error('Error fetching accounting centers:', err);
    } finally {
      setLoadingAccountingCenters(false);
    }
  };

  // Fetch expense and revenue status
  const fetchExpenseRevenueStatus = async () => {
    if (!budgetId) return;

    try {
      // Fetch expense categories to check if expense is activated
      setLoadingExpense(true);
      const expenseResponse = await expenseRevenueService.getExpenseCategories(budgetId);
      setExpenseToggle(expenseResponse.data && expenseResponse.data.length > 0);
    } catch (error) {
      console.error('Error fetching expense status:', error);
      setExpenseToggle(false);
    } finally {
      setLoadingExpense(false);
    }

    try {
      // Fetch revenue categories to check if revenue is activated
      setLoadingRevenue(true);
      const revenueResponse = await revenueCategoryService.getRevenueCategories(budgetId);
      setRevenueToggle(revenueResponse.data && revenueResponse.data.length > 0);
    } catch (error) {
      console.error('Error fetching revenue status:', error);
      setRevenueToggle(false);
    } finally {
      setLoadingRevenue(false);
    }
  };

  useEffect(() => {
    // Set breadcrumbs when component mounts
    dispatch(
      setBreadcrumbs([
        {
          title: 'Budgets',
          path: PAGE.BUDGETS.path
        },
        {
          title: 'Budget Overview',
          path: `/budgets/${budgetId}`
        }
      ])
    );
  }, [dispatch, budgetId]);

  const fetchBudgetDetails = async () => {
    if (!budgetId) {
      setError('Budget ID not found');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log('Fetching budget with ID:', budgetId);
      const { data } = await budgetService.getBudget(budgetId);

      setBudget(data);
    } catch (err) {
      console.error('Error fetching budget:', err);
      setError('Failed to load budget details');
    } finally {
      setLoading(false);
    }
  };

  // Update your fetchBankAccounts function to transform the data
  const fetchBankAccounts = async () => {
    if (budgetId) {
      try {
        const { data: bankAccountsData } = await bankAccountService.getBankAccounts(budgetId);

        // Transform the data into the expected format
        const formattedBankAccounts = bankAccountsData.map((account) => ({
          id: account._id,
          title: `${account.accountName}`,
          icon: vaultIcon,
          data: [
            {
              subtitle: 'Remaining balance',
              value: String(account.initialAmount || 0)
            }
          ]
        }));

        setBankAccounts(formattedBankAccounts);
      } catch (error) {
        console.error('Error fetching bank accounts:', error);
        setBankAccounts([]);
      }
    }
  };

  useEffect(() => {
    fetchBudgetDetails();
    fetchBankAccounts();
    fetchAccountingCenters();
    fetchExpenseRevenueStatus();
  }, [budgetId]);

  const handleDeleteClick = () => {
    setDeleteConfirmModal(true);
  };

  const handleDeleteBudget = async () => {
    try {
      await budgetService.deleteBudget(budgetId!);

      toast.success('Budget deleted successfully');
      navigate('/budgets');
    } catch (err) {
      console.error('Error deleting budget:', err);
      alert('Failed to delete budget');
    }
  };

  const handleAddBankClick = () => {
    if (budgetId) {
      navigate(`/budgets/${budgetId}/bank-accounts/add`);
    }
  };

  const handleSeeAllBankClick = () => {
    if (budgetId) {
      navigate(`/budgets/${budgetId}/bank-accounts`);
    }
  };

  const handleBankAccountClick = (bankAccountId: string) => {
    if (budgetId) {
      navigate(`/budgets/${budgetId}/bank-accounts/${bankAccountId}`);
    }
  };

  const handleActivateAC = async (value: string) => {
    try {
      const { data: responseData } = await accountingCenterService.activateACs(budgetId || '', { type: value });
      toast.success(`${responseData?.name || 'Accounting center'} activated successfully`);

      // Update the toggle state on success
      setAccountingCentersToggle({
        ...accountingCentersToggle,
        [value]: true
      });

      // Refresh accounting centers data
      fetchAccountingCenters();

      return true;
    } catch (error) {
      console.error('Error in handleActivateAC:', error);
      toast.error('Failed to activate accounting center');
      return false;
    }
  };

  const handleDeactivateAC = async (value: string) => {
    setSelectedAC(value);
    setDeactivateACConfirmModal(true);
    return false; // Return false to prevent immediate toggle change
  };

  const confirmDeactivateAC = async () => {
    if (!selectedAC) return;

    try {
      // Call the API to deactivate the accounting center
      await accountingCenterService.deactivateAC(budgetId || '', { type: selectedAC });

      toast.success('Accounting center deactivated successfully');

      // Update the toggle state on success
      setAccountingCentersToggle({
        ...accountingCentersToggle,
        [selectedAC]: false
      });

      // Close the modal and reset selectedAC
      setDeactivateACConfirmModal(false);
      setSelectedAC(null);
    } catch (error) {
      console.error('Error in confirmDeactivateAC:', error);
      toast.error('Failed to deactivate accounting center');
    }
  };

  const handleExpenseRevenueToggle = async (value: string, checked: boolean) => {
    try {
      // For now, just update the toggle state
      // API integration can be added later
      setExpenseToggle(checked);
      setRevenueToggle(checked);

      const itemName = value === 'EXPENSE' ? 'Expense' : 'Revenue';
      const action = checked ? 'activated' : 'deactivated';
      toast.success(`${itemName} ${action} successfully`);

      return true;
    } catch (error) {
      console.error('Error in handleExpenseRevenueToggle:', error);
      toast.error('Failed to update expense/revenue setting');
      return false;
    }
  };

  // Calculate chart data based on budget values
  const calculateChartData = () => {
    if (!budget) {
      return {
        bmevChartData: [{ key: 'remaining', value: 100, fill: '#32ADE6' }],
        btrtChartData: [{ key: 'target', value: 100, fill: '#32ADE6' }]
      };
    }

    // Calculate percentages for BMEV (Budget Maximum Expense Value)
    let bmevData = [];
    if (budget.bmev !== undefined) {
      const bmevTotal = budget.bmev || 0;
      const bmevUsed = budget.bmev_used || 0;
      const bmevRemaining = Math.max(0, bmevTotal - bmevUsed);

      // Only add remaining if there's a total budget value
      if (bmevTotal > 0) {
        const bmevRemainingPercent = Math.round((bmevRemaining / bmevTotal) * 100);
        bmevData.push({ key: 'remaining', value: bmevRemainingPercent, fill: '#32ADE6' });

        const bmevUsedPercent = Math.round((bmevUsed / bmevTotal) * 100);
        bmevData.push({ key: 'spent', value: bmevUsedPercent, fill: '#FF2D55' });
      } else {
        // Default value if bmev is 0
        bmevData.push({ key: 'remaining', value: 100, fill: '#32ADE6' });
      }
    } else {
      // Default value if bmev is not defined
      bmevData.push({ key: 'remaining', value: 100, fill: '#32ADE6' });
    }

    console.log('bmevData:', bmevData);

    // Calculate percentages for BTRT (Budget Total Revenue Target)
    let btrtData = [];
    if (budget.btrt !== undefined) {
      const btrtTotal = budget.btrt || 0;
      // Using btrt_achieved as the API response shows this field, not btrt_used
      const btrtAchieved = budget.btrt_achieved || 0;

      // Only calculate percentages if there's a target
      if (btrtTotal > 0) {
        const btrtAchievedPercent = Math.round((btrtAchieved / btrtTotal) * 100);

        // Add target (always 100%)
        btrtData.push({ key: 'target', value: 100 - btrtAchievedPercent, fill: '#32ADE6' });

        btrtData.push({ key: 'achieved', value: btrtAchievedPercent, fill: '#34C759' });
      } else {
        // Default value if btrt is 0
        btrtData.push({ key: 'target', value: 100, fill: '#32ADE6' });
      }
    } else {
      // Default value if btrt is not defined
      btrtData.push({ key: 'target', value: 100, fill: '#32ADE6' });
    }

    return {
      bmevChartData: bmevData,
      btrtChartData: btrtData
    };
  };

  const { bmevChartData, btrtChartData } = calculateChartData();

  const bmevChartConfig = {
    remaining: {
      label: 'Remaining Budget',
      color: '#32ADE6'
    },
    spent: {
      label: 'Budget Spent',
      color: '#FF2D55'
    }
  } satisfies ChartConfig;

  const btrtChartConfig = {
    target: {
      label: 'Revenue Target',
      color: '#32ADE6'
    },
    achieved: {
      label: 'Achieved Revenue',
      color: '#34C759'
    }
  } satisfies ChartConfig;

  // Bank accounts data

  // Expense and revenues
  const expenseAndRevenues: ListItem[] = [];

  const handleActivateExpense = async () => {
    try {
      setLoadingExpense(true);
      await expenseRevenueService.activateExpenseCategories(budgetId || '', { type: 'EXPENSE_CATEGORY' });
      toast.success('Expense activated successfully');
      setExpenseToggle(true);
      return true;
    } catch (error) {
      console.error('Error activating expense:', error);
      toast.error('Failed to activate expense');
      return false;
    } finally {
      setLoadingExpense(false);
    }
  };

  const handleDeactivateExpense = async () => {
    setDeactivateExpenseConfirmModal(true);
    return false; // Return false to prevent immediate toggle change
  };

  const confirmDeactivateExpense = async () => {
    try {
      setLoadingExpense(true);
      await expenseRevenueService.deactivateExpenseCategory(budgetId || '', {});
      toast.success('Expense deactivated successfully');
      setExpenseToggle(false);
      setDeactivateExpenseConfirmModal(false);
    } catch (error) {
      console.error('Error deactivating expense:', error);
      toast.error('Failed to deactivate expense');
    } finally {
      setLoadingExpense(false);
    }
  };

  const handleActivateRevenue = async () => {
    try {
      setLoadingRevenue(true);
      await revenueCategoryService.activateRevenueCategories(budgetId || '', { type: 'REVENUE_CATEGORY' });
      toast.success('Revenue activated successfully');
      setRevenueToggle(true);
      return true;
    } catch (error) {
      console.error('Error activating revenue:', error);
      toast.error('Failed to activate revenue');
      return false;
    } finally {
      setLoadingRevenue(false);
    }
  };

  const handleDeactivateRevenue = async () => {
    setDeactivateRevenueConfirmModal(true);
    return false; // Return false to prevent immediate toggle change
  };

  const confirmDeactivateRevenue = async () => {
    try {
      setLoadingRevenue(true);
      await revenueCategoryService.deactivateRevenueCategory(budgetId || '', {});
      toast.success('Revenue deactivated successfully');
      setRevenueToggle(false);
      setDeactivateRevenueConfirmModal(false);
    } catch (error) {
      console.error('Error deactivating revenue:', error);
      toast.error('Failed to deactivate revenue');
    } finally {
      setLoadingRevenue(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p>Loading budget details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  return (
    <>
      <MobileAppBar title="Budget Overview" onRightIconClick={handleDeleteClick} rightIcon={binIcon} />
      <div className="p-4">
        {/* BUDGET INFORMATION - MOBILE */}
        <div className="flex items-center justify-between gap-5 mb-6 md:hidden">
          <div className="flex items-center gap-3">
            <img src={budgetIcon} alt="budget-icon" width={40} height={40} />
            <div>
              <h4 className="font-semibold text-base">{budget?.name || 'BUDGET NAME'}</h4>
              <p className="text-secondary text-sm font-medium">{budget?.type || 'Company'}</p>
            </div>
          </div>

          <button type="button" onClick={() => navigate(`/budgets/edit/${budgetId}`)}>
            <img src={editIcon} alt="edit-icon" width={24} height={24} />
          </button>
        </div>

        <div className="md:flex md:gap-7">
          {/* BUDGET INFORMATION - WEB */}
          <BudgetInfoCard
            name={budget?.name}
            type={budget?.type}
            description={budget?.description}
            currency={budget?.currency}
            startDate={formatDate(budget?.start_date)}
            endDate={formatDate(budget?.end_date)}
            isAutoRenewal={budget?.is_auto_renewal}
            onEdit={() => navigate(`/budgets/edit/${budgetId}`)}
            onDelete={handleDeleteClick}
          />

          {/* GRAPH OVERVIEW */}
          <div className="md:w-[40%] md:bg-white md:p-[0.85rem] md:rounded-[16px]">
            <header className="w-full flex items-center justify-between mb-3 md:mb-4">
              <h3 className="text-base font-bold text-primary md:text-foreground">Graph Overview</h3>
              <Tabs defaultValue="percentage" value={graphViewMode} onValueChange={setGraphViewMode}>
                <TabsList className="bg-lightPurple-200 h-auto py-[0.2rem]">
                  <TabsTrigger value="percentage" className="group md:py-[0.3rem]">
                    <img
                      src={percentageIcon}
                      alt="percentage-icon"
                      width={13}
                      height={17}
                      className="group-data-[state=active]:brightness-[100]"
                    />
                  </TabsTrigger>
                  <TabsTrigger value="euro" className="group md:py-[0.3rem]">
                    <img
                      src={euroIcon}
                      alt="euro-icon"
                      width={12}
                      height={12}
                      className="w-[12px] h-[12px] group-data-[state=active]:brightness-[100]"
                    />
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </header>
            <div className="flex items-start justify-between gap-3">
              <PieGraph
                title="Remaining BMEV"
                titleValue={(() => {
                  if (graphViewMode === 'percentage') {
                    return bmevChartData.length > 0 ? `${bmevChartData[0].value}%` : '0%';
                  } else {
                    // Euro mode - calculate actual value
                    const bmevTotal = budget?.bmev || 0;
                    const bmevUsed = budget?.bmev_used || 0;
                    const bmevRemaining = Math.max(0, bmevTotal - bmevUsed);
                    return `€${bmevRemaining.toLocaleString()}`;
                  }
                })()}
                chartConfig={bmevChartConfig}
                chartData={bmevChartData}
                className="bg-white rounded-[16px] p-[0.85rem] flex-1 md:border border-lightGray md:py-10 md:flex md:items-center md:justify-center md:flex-col"
                minHeight={180}
                outerRadius={64}
              />

              <PieGraph
                title="Achieved BTRT"
                titleValue={(() => {
                  if (graphViewMode === 'percentage') {
                    const achievedItem = btrtChartData.find((item) => item.key === 'achieved');
                    return achievedItem ? `${achievedItem.value}%` : '0%';
                  } else {
                    // Euro mode - display the actual achieved value
                    const btrtAchieved = budget?.btrt_achieved || 0;
                    return `€${btrtAchieved.toLocaleString()}`;
                  }
                })()}
                chartConfig={btrtChartConfig}
                chartData={btrtChartData}
                variant="primary"
                className="bg-white rounded-[16px] p-[0.85rem] flex-1 md:border border-lightGray md:py-10 md:flex md:items-center md:justify-center md:flex-col"
                minHeight={180}
                outerRadius={64}
              />
            </div>
          </div>
        </div>

        {/* GENERAL INFORMATION - MOBILE */}
        {isMobileView && (
          <BudgetGeneralInfoAccordion
            description={budget?.description}
            bmevThreshold={budget?.bmev_alert_threshold}
            btrtThreshold={budget?.btrt_alert_threshold}
            currency={budget?.currency}
            autoRenewal={budget?.is_auto_renewal}
            startDate={formatDate(budget?.start_date)}
            endDate={formatDate(budget?.end_date)}
          />
        )}

        <div className="md:flex md:items-stretch md:justify-between md:gap-6 md:min-h-[300px]">
          {/* BANK ACCOUNTS */}
          <OverviewList
            title="Bank Accounts"
            items={bankAccounts}
            icon={vaultIcon}
            addButtonIcon={plusIcon}
            addButtonTitle="ADD BANK ACCOUNTS"
            valuePrefix={budget?.currency === 'EUR' ? '€' : '$'}
            valueSuffix="k"
            onAddClick={handleAddBankClick}
            className="mt-8 bg-lightPurple md:w-[33%] rounded-[16px] md:flex-2 md:bg-white md:border md:border-lightGray"
            onSeeAllClick={handleSeeAllBankClick}
            onItemClick={(id) => handleBankAccountClick(id)}
          />

          {/* ACCOUNTING CENTERS */}
          {/* <OverviewList
            title="Accounting Centers"
            items={accountingCenters}
            icon={buildingIcon}
            addButtonIcon={plusIcon}
            addButtonTitle="ADD ACCOUNTING CENTERS"
            valuePrefix={budget?.currency === 'EUR' ? '€' : '$'}
            valueSuffix="k"
            onAddClick={() => {}}
            className="mt-8 bg-lightPurple md:w-[33%] rounded-[16px] md:flex-1 md:bg-white md:border md:border-lightGray"
            onItemClick={() => {}}
          /> */}

          <div className="mt-8 bg-lightPurple md:w-[33%] rounded-[16px] md:flex-1 md:bg-white md:border md:border-lightGray">
            <div className="p-4 px-2 md:px-4 pb-2 md:pb-4">
              {/* HEADER */}
              <div className="flex justify-between mb-[0.85rem]">
                <p className="pl-2 text-xs text-lightPurple-300 font-medium md:text-foreground md:font-bold md:text-base">
                  Accounting Centers
                </p>
              </div>

              {/* ACCOUNTING CENTER ITEMS */}
              <div className="border border-white rounded-[8px] space-y-0 px-4 md:px-0 md:space-y-4">
                {loadingAccountingCenters ? (
                  <p className="text-secondary text-sm text-center py-6">Loading...</p>
                ) : (
                  ACCOUNTING_CENTERS.map((item) => {
                    // Find the accounting center in the fetched data
                    const acCenter = accountingCenters.find((ac: any) => ac.type === item.value && ac?.is_default);

                    return (
                      <AccountCenterToggleItem
                        key={item.value}
                        budgetId={budgetId}
                        item={item}
                        acCenter={acCenter}
                        value={accountingCentersToggle[item.value]}
                        defaultChecked={acCenter?.is_default}
                        onChange={async (checked: boolean, setChecked: any) => {
                          if (checked) {
                            // Trying to activate
                            const success = await handleActivateAC(item.value);
                            // The toggle will update via state if API succeeds
                          } else {
                            // Trying to deactivate
                            handleDeactivateAC(item.value);
                            // The toggle state won't change until confirmation
                          }
                        }}
                      />
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {/* EXPENSE AND REVENUE */}
          <div className="mt-8 bg-lightPurple md:w-[33%] rounded-[16px] md:flex-1 md:bg-white md:border md:border-lightGray">
            <div className="p-4 px-2 md:px-4 pb-2 md:pb-4">
              {/* HEADER */}
              <div className="flex justify-between mb-[0.85rem]">
                <p className="pl-2 text-xs text-lightPurple-300 font-medium md:text-foreground md:font-bold md:text-base">
                  Expense & Revenue
                </p>
              </div>

              {/* EXPENSE AND REVENUE ITEMS */}
              <div className="border border-white rounded-[8px] space-y-0 px-4 md:px-0 md:space-y-4">
                <ToggleItem
                  key={EXPENSE_ITEM.value}
                  budgetId={budgetId}
                  item={EXPENSE_ITEM}
                  value={expenseToggle}
                  defaultChecked={false}
                  onChange={async (checked: boolean, setChecked: any) => {
                    if (checked) {
                      // Trying to activate
                      await handleActivateExpense();
                    } else {
                      // Trying to deactivate
                      await handleDeactivateExpense();
                    }
                  }}
                  onItemClick={() => {
                    // Navigate to Expense Categories page if toggle is active
                    if (expenseToggle && budgetId) {
                      navigate(`/budgets/${budgetId}/expense-categories`);
                    }
                  }}
                />
                <ToggleItem
                  key={REVENUE_ITEM.value}
                  budgetId={budgetId}
                  item={REVENUE_ITEM}
                  value={revenueToggle}
                  defaultChecked={false}
                  onChange={async (checked: boolean, setChecked: any) => {
                    if (checked) {
                      // Trying to activate
                      await handleActivateRevenue();
                    } else {
                      // Trying to deactivate
                      await handleDeactivateRevenue();
                    }
                  }}
                  onItemClick={() => {
                    // Add navigation or other click handling here if needed
                    console.log(`Clicked on ${REVENUE_ITEM.title}`);
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="md:flex md:items-stretch md:justify-between md:gap-6">
          {/* INVOICE */}
          <section className="mt-7 md:flex-1 md:bg-white md:p-4 md:rounded-[16px] md:border md:border-lightGray md:flex md:flex-col">
            <p className="text-base font-bold">Invoice</p>
            <div className="bg-lightPurple rounded-[16px] p-6 flex items-center justify-center flex-col gap-3 mt-4 md:bg-white md:flex-1">
              <div className="flex flex-col items-center justify-center md:flex-row md:gap-4">
                <img src={noInvoiceIcon} alt="no invoice icon" className="md:w-[40px] md:h-[40px]" />
                <span className="mt-[3px] text-xs font-medium text-center leading-[16.5px] md:text-left">
                  <span className="md:text-base md:font-medium md:block mb-[-3px] md:text-foreground">
                    There are no Invoice to show.
                  </span>{' '}
                  <span className="md:text-secondary md:text-base md:font-medium">
                    {' '}
                    Please create a new invoice to allocate budget!
                  </span>
                </span>
              </div>
              <Button variant="primary-reverse" className="rounded-full font-semibold px-5 text-base">
                Create an Invoice
              </Button>
            </div>
          </section>

          {/* COMMITMENTS */}
          <div className="mt-7 md:bg-white md:p-4 md:rounded-[16px] md:border md:border-lightGray">
            <p className="text-base font-bold mb-4">Commitments</p>

            <Calendar
              mode="single"
              selected={date}
              onSelect={setDate}
              className="rounded-[16px] border border-lightGray py-6"
            />
          </div>
        </div>

        {/* Delete Confirm Modal */}
        {deleteConfirmModal && (
          <InfoModal
            isOpen={deleteConfirmModal}
            variant="confirm"
            title="Are you sure want to Delete this Budget?"
            message={<p>Deleted budget will removed permanently and unable to restore</p>}
            buttonText="Delete"
            onButtonClick={handleDeleteBudget}
            onClose={() => setDeleteConfirmModal(false)}
            icon={deleteBudgetIcon}
          />
        )}

        {/* Deactivate Expense Confirm Modal */}
        {deactivateExpenseConfirmModal && (
          <InfoModal
            isOpen={deactivateExpenseConfirmModal}
            variant="confirm"
            title="Are you sure want to Deactivate Expense?"
            message={<p>All Expense under this category will automatically deleted</p>}
            buttonText="Delete"
            onButtonClick={confirmDeactivateExpense}
            onClose={() => {
              setDeactivateExpenseConfirmModal(false);
            }}
            icon={toggleACOffIcon}
          />
        )}

        {/* Deactivate Revenue Confirm Modal */}
        {deactivateRevenueConfirmModal && (
          <InfoModal
            isOpen={deactivateRevenueConfirmModal}
            variant="confirm"
            title="Are you sure want to Deactivate Revenue?"
            message={<p>All Revenue under this category will automatically deleted</p>}
            buttonText="Delete"
            onButtonClick={confirmDeactivateRevenue}
            onClose={() => {
              setDeactivateRevenueConfirmModal(false);
            }}
            icon={toggleACOffIcon}
          />
        )}

        {/* Deactivate AC Confirm Modal */}
        {deactivateACConfirmModal && (
          <InfoModal
            isOpen={deactivateACConfirmModal}
            variant="confirm"
            title="Are you sure want to Deactivate Department Accounting Center Category?"
            message={<p>All Accounting Center under Department Category will automatically deleted</p>}
            buttonText="Delete"
            onButtonClick={confirmDeactivateAC}
            onClose={() => {
              setDeactivateACConfirmModal(false);
            }}
            icon={toggleACOffIcon}
          />
        )}
      </div>
    </>
  );
};

export default DetailPage;
